<template>
  <div v-if="visible.length" class="notices" role="status" aria-live="polite">
    <div v-for="n in visible" :key="n.id" class="notices__item" :class="`notices__item--${tone(n.kind)}`">
      <div class="notices__title">{{ title(n) }}</div>
      <div class="notices__body">{{ body(n) }}</div>
      <div class="notices__actions">
        <span class="notices__time">{{ time(n.created_at) }}</span>
        <button class="notices__ok" @click="dismiss(n)">{{ $t('notices.ok') }}</button>
      </div>
    </div>
    <button v-if="unread.length > visible.length || unread.length > 1" class="notices__all" @click="dismissAll">
      {{ $t('notices.read-all', { n: unread.length }) }}
    </button>
  </div>
</template>

<script>
// Platform notifications (2026-10-18): the steps of a pool deposit or withdrawal and other events the user must
// know about. They pop up on any page while unread (the list lives on the server, so they reach a user who was
// away), arrive at once over the exchange WebSocket and are re-read every minute as a safety net.
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import store from '@/vuex/index';
import * as lp from '@/js/services/pool.service';
import { ExchangeSocket } from '@/js/services/exchange.service';
import { fromUnits } from '@/js/helpers/units';

export default {
  setup() {
    const isLoggedIn = computed(() => store.getters['auth/isLoggedIn']);
    const token = computed(() => store.state.auth?.user?.access_token || '');
    const { te } = useI18n();
    return { isLoggedIn, token, te };
  },
  data: () => ({ unread: [], socket: null, timer: null }),
  computed: {
    visible() {
      return this.unread.slice(0, 3);
    },
  },
  watch: {
    isLoggedIn: {
      immediate: true,
      handler(v) {
        this.stop();
        if (v) this.start();
      },
    },
  },
  beforeUnmount() {
    this.stop();
  },
  methods: {
    start() {
      this.load();
      this.timer = setInterval(this.load, 60000);
      try {
        this.socket = new ExchangeSocket((ch, data) => {
          if (ch === 'notifications' && data && !this.unread.some((n) => n.id === data.id)) {
            this.unread = [data, ...this.unread];
          }
        });
        if (this.token) this.socket.auth(this.token);
        this.socket.sub('notifications');
      } catch (e) {
        this.socket = null;
      }
    },
    stop() {
      clearInterval(this.timer);
      this.timer = null;
      this.socket && this.socket.close();
      this.socket = null;
      this.unread = [];
    },
    async load() {
      try {
        const r = await lp.getNotifications(true);
        this.unread = r.data || [];
      } catch (e) {
        /* the exchange may be off: no notifications then */
      }
    },
    async dismiss(n) {
      this.unread = this.unread.filter((x) => x.id !== n.id);
      try {
        await lp.markRead([n.id]);
      } catch (e) {
        /* shown again on the next load */
      }
    },
    async dismissAll() {
      this.unread = [];
      try {
        await lp.markRead([]);
      } catch (e) {
        /* ignore */
      }
    },
    tone(kind) {
      if (kind === 'pool_rejected') return 'bad';
      if (kind === 'pool_paid' || kind === 'pool_deposit') return 'good';
      return 'info';
    },
    params(n) {
      const d = n.data || {};
      const reasonKey = `exchange.pool.reasons.${d.reason}`;
      return {
        id: d.id,
        asset: d.asset || '',
        amount: d.amount ? fromUnits(d.amount, 2) : '',
        value: d.value_usd ? lp.fromE18(d.value_usd, 2) : '',
        shares: d.shares ? lp.fromE18(d.shares, 2) : '',
        reason: this.te(reasonKey) ? this.$t(reasonKey) : this.$t('exchange.pool.reasons.other'),
      };
    },
    title(n) {
      const k = `notices.${n.kind}.title`;
      return this.te(k) ? this.$t(k, this.params(n)) : n.title;
    },
    body(n) {
      const k = `notices.${n.kind}.body`;
      return this.te(k) ? this.$t(k, this.params(n)) : n.body;
    },
    time: (t) => (t ? new Date(t).toLocaleString() : ''),
  },
};
</script>

<style scoped lang="scss">
.notices {
  position: fixed;
  right: 1.6rem;
  bottom: calc(1.6rem + env(safe-area-inset-bottom, 0px));
  z-index: 1100;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.8rem;
  width: min(38rem, calc(100vw - 3.2rem));

  &__item {
    width: 100%;
    background: #fff;
    border-radius: 1.2rem;
    box-shadow: 0 0.6rem 2.4rem rgba(16, 24, 40, 0.18);
    padding: 1.2rem 1.4rem;
    border-left: 0.4rem solid #3b82f6;
    font-size: 1.35rem;
    color: #1f2937;

    &--good {
      border-left-color: #1e8449;
    }
    &--bad {
      border-left-color: #c53030;
    }
  }

  &__title {
    font-weight: 600;
    font-size: 1.45rem;
  }

  &__body {
    margin-top: 0.4rem;
    line-height: 1.45;
    color: #4b5563;
  }

  &__actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 0.8rem;
  }

  &__time {
    color: #9ca3af;
    font-size: 1.2rem;
  }

  &__ok,
  &__all {
    border: none;
    background: #eef2ff;
    color: #1d4ed8;
    border-radius: 0.8rem;
    padding: 0.5rem 1.2rem;
    font-size: 1.3rem;
    cursor: pointer;
  }

  &__all {
    background: #1f2937;
    color: #fff;
  }

  @media (max-width: 768px) {
    // above the exchange page's buy/sell bar
    bottom: calc(8.4rem + env(safe-area-inset-bottom, 0px));
  }
}
</style>
